import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccountBalance } from '@ibk/shared-config';
import { useTranslation } from '@ibk/shared-translation';
import { formatCurrency } from '@ibk/shared-utils';
import { useBenefitContract, useRecharge } from '../../../../../hooks';
import { RechargePaymentMethod } from '../../../../../types';
import { useBankSlipPayment } from './useBankSlipPayment';
import { usePaymentCodeWithRetry } from './usePaymentCodeWithRetry';
import { usePixPayment } from './usePixPayment';
export function usePaymentContent({ rechargeId }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [selectedMethod, setSelectedMethod] = useState(null);
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [hasSufficientBalance, setHasSufficientBalance] = useState(false);
    const [isNavigating, setIsNavigating] = useState(false);
    const { balance, isLoading: isLoadingBalance, hasAccount } = useAccountBalance();
    const { contractData, isLoading: isContractLoading } = useBenefitContract();
    const { recharge, isLoading: isRechargeLoading } = useRecharge({
        voucherContractId: contractData?.id,
        rechargeId,
    });
    const { navigateToPixFlow } = usePixPayment();
    const { navigateToBankSlipFlow } = useBankSlipPayment();
    const { startRetry, isRetrying } = usePaymentCodeWithRetry({
        params: contractData?.id && rechargeId
            ? { voucherContractId: contractData.id, voucherContractRechargeId: rechargeId }
            : null,
        paymentMethod: selectedMethod,
        onSuccess: (copyAndPasteValue) => {
            if (!selectedMethod)
                return;
            setIsNavigating(true);
            try {
                if (selectedMethod === RechargePaymentMethod.ACCOUNT_BALANCE) {
                    navigate('/conta-pj/pagar/pix-copia-e-cola', {
                        state: {
                            code: copyAndPasteValue,
                        },
                    });
                }
                else if (selectedMethod === RechargePaymentMethod.PIX) {
                    navigateToPixFlow(rechargeId);
                }
                else if (selectedMethod === RechargePaymentMethod.BANK_SLIP) {
                    navigateToBankSlipFlow(rechargeId);
                }
            }
            catch (error) {
                setIsNavigating(false);
                setAlertMessage(t('payment.error.process-payment'));
                setShowAlert(true);
            }
        },
        onError: () => {
            if (!selectedMethod)
                return;
            if (selectedMethod === RechargePaymentMethod.ACCOUNT_BALANCE) {
                setAlertMessage(t('payment.error.cannot-proceed'));
                setShowAlert(true);
            }
            else if (selectedMethod === RechargePaymentMethod.PIX) {
                navigateToPixFlow(rechargeId);
            }
            else if (selectedMethod === RechargePaymentMethod.BANK_SLIP) {
                navigateToBankSlipFlow(rechargeId);
            }
        },
    });
    const breadcrumbs = [
        { label: t('recharge.breadcrumb.benefits'), href: '/beneficios' },
        { label: t('recharge.breadcrumb.new-recharge'), isActive: true },
    ];
    const currentStepConfig = {
        id: 'payment',
        title: t('payment.step.title'),
        number: 5,
        status: 'active',
    };
    useEffect(() => {
        const totalValue = recharge?.recharge?.totalValue ?? 0;
        setHasSufficientBalance(hasAccount && balance >= totalValue);
    }, [balance, hasAccount, recharge?.recharge?.totalValue]);
    useEffect(() => {
        if (hasSufficientBalance) {
            setSelectedMethod(RechargePaymentMethod.ACCOUNT_BALANCE);
        }
        else {
            setSelectedMethod(RechargePaymentMethod.PIX);
        }
    }, [hasSufficientBalance]);
    const isLoading = isContractLoading || isRechargeLoading || isLoadingBalance || isRetrying || isNavigating;
    const handleMethodSelect = (method) => {
        setSelectedMethod(method);
    };
    const handlePayment = async () => {
        if (!selectedMethod)
            return;
        if (!contractData?.id || !rechargeId) {
            setAlertMessage(t('payment.error.process-payment'));
            setShowAlert(true);
            return;
        }
        startRetry();
    };
    const handleCloseAlert = () => {
        setShowAlert(false);
        setAlertMessage('');
    };
    const getAccountBalanceDescription = () => {
        if (isLoadingBalance)
            return t('payment.method.account.loading');
        return hasSufficientBalance
            ? formatCurrency(balance)
            : `${formatCurrency(balance)} - ${t('payment.method.account.insufficient')}`;
    };
    return {
        selectedMethod,
        showAlert,
        alertMessage,
        hasSufficientBalance,
        hasAccount,
        isLoadingBalance,
        recharge,
        isLoading,
        isRetrying,
        breadcrumbs,
        currentStepConfig,
        accountBalanceDescription: getAccountBalanceDescription(),
        totalValue: formatCurrency(recharge?.recharge?.totalValue ?? 0),
        handleMethodSelect,
        handlePayment,
        handleCloseAlert,
    };
}
